import domToImage from 'dom-to-image';
import JSZip from 'jszip';
import { trackEventWithUserProperties } from '@/utils/mixpanel';
import { EXTENSION_TYPES } from '@/utils/constants';
import { CONTENT_EXCEPTIONS, PIECHART_EXCEPTIONS } from '@/utils/export';
import * as Sentry from '@sentry/browser';

export default {
  data() {
    return {
      exportConfig: {
        style: {
          padding: '8px',
          overflow: 'hidden',
        },
        filter: this.clearCardHeaderActions,
        cacheBust: true,
      },
      exportExtensions: [
        {
          type: EXTENSION_TYPES.PNG.name,
          title: this.$t('mixins.exportModel.label.exportLabelType', { type: EXTENSION_TYPES.PNG.name }),
        },
      ],
      exportLabel: this.$t('mixins.exportModel.label.exportLabel'),
      loadingExportStatus: false,
    };
  },
  computed: {
    existsCardToExport() {
      return Boolean(this.cards.length) && Boolean(Array.from(window.document.querySelectorAll('div.export-card')).length);
    },
    isLoadingExportComponents() {
      return this.loadingExportStatus;
    },
    getExportExtensionOptions() {
      return this.exportExtensions.map((extension, index) => (
        {
          order: index + 1,
          name: extension.type,
          function: () => (this.exportComponent(extension)),
        }
      ));
    },
    exportOptionsData() {
      const exportExtensionOptions = this.exportExtensions.map((extension, index) => (
        {
          order: index + 1,
          name: extension.type,
          function: () => (this.exportComponent(extension, true)),
        }
      ));

      return [
        {
          order: 5,
          name: 'Baixar em',
          iconname: 'download',
          function: () => ([]),
          child: exportExtensionOptions,
          trackName: 'ST CardMenu Export Click',
          trackProperties: {
            title: this.card?.props?.title,
            widget: this.card?.component,
          },
          isExpandedModeOption: true,
        },
      ];
    },
    exportWithCSVOptionsData() {
      const exportExtensionOptions = this.exportExtensions.map((extension, index) => (
        {
          order: index + 1,
          name: extension.type,
          function: () => (this.exportComponent(extension, true)),
        }
      ));

      exportExtensionOptions.push({
        order: exportExtensionOptions.length + 1,
        name: 'CSV',
        function: () => (this.exportComponentCSV()),
      });

      return [
        {
          order: 5,
          name: 'Baixar em',
          iconname: 'download',
          function: () => ([]),
          child: exportExtensionOptions,
          trackName: 'ST CardMenu Export Click',
          trackProperties: {
            title: this.card?.props?.title,
            widget: this.card?.component,
          },
          isExpandedModeOption: true,
        },
      ];
    },
  },
  methods: {
    async exportAllComponents() {
      const zip = new JSZip();

      const contentList = Array.from(window.document.querySelectorAll('div.export-card > div > div > div'))
        .filter(content => {
          const card = content.__vue__?.card;
          const isShareableComponent = card?.hasOwnProperty('shareable');

          return !CONTENT_EXCEPTIONS.includes(card?.component) && !isShareableComponent;
        });
      const processContentList = contentList.map(async (content, index) => {
        this.loadingExportStatus = true;
        let dataURL;
        try {
          dataURL = content.classList.contains('make-a-copy')
            ? await this.getClonedComponentDataURL(content.__vue__, this.exportConfig)
            : await domToImage.toPng(content, this.exportConfig);
        } catch (error) {
          Sentry.captureException(new Error(`Failed to convert ${content?.__vue__?.card?.component}`));
          return error;
        }
        const stream = await fetch(dataURL);
        const reader = stream.body.getReader();
        return reader.read().then(({ value }) => zip.file(`${String(index + 1).padStart(3, '0')}_${content?.__vue__?.title}.png`, value));
      });

      Promise.all(processContentList).then(() => zip.generateAsync({ type: 'base64' })
        .then(zipURL => {
          this.downloadConvertedZip(zipURL);
        }))
        .catch(error => {
          console.error({
            error,
          });
          this.loadingExportStatus = false;
        });
    },
    async exportComponent(extension, cloneComponent = false) {
      const dataURL = cloneComponent
        ? await this.getClonedComponentDataURL(this, this.exportConfig)
        : await domToImage.toPng(this.$el, this.exportConfig);
      this.downloadConvertedImage(dataURL, extension.type);
      trackEventWithUserProperties('ST Card Options Export', {
        Title: this.card?.props?.title,
        Widget: this.card?.component,
        Type: extension.type,
      });
    },
    async exportSelectedComponent(extension, component) {
      const dataURL = await domToImage.toPng(component.$el, this.exportConfig);
      this.downloadConvertedImage(dataURL, extension.type, component.card?.props?.text);
      trackEventWithUserProperties('ST Card Options Export', {
        Title: component.card?.props?.title,
        Widget: component.card?.component,
        Type: extension.type,
      });
    },
    clearCardHeaderActions(node) {
      const classes = ['__header-actions', '__controls', '__edit-title', 'options-button', 'v-counter'];
      if (!node.tagName) {
        return true;
      }

      const nodeClass = node?.getAttributeNode('class') ? node?.getAttributeNode('class').value : '';
      return !classes.some(classFragment => nodeClass.indexOf(classFragment) >= 0);
    },
    isHeaderOrContent(node) {
      const classes = ['card-with-title__header', 'content'];
      return classes.some(classFragment => node.classList ? node.classList.contains(classFragment) : false);
    },
    removePieChartLayoutHeight(node) {
      if (node.lastChild.lastChild.classList.contains('pie-chart-legend')) {
        node.lastChild.style.setProperty('height', 'auto');
        node.lastChild.lastChild.style.setProperty('height', 'auto');
      }
    },
    restorePieChartLayotHeight(node) {
      if (node.lastChild.lastChild.classList.contains('pie-chart-legend')) {
        node.lastChild.style.setProperty('height', '100%');
        node.lastChild.lastChild.style.setProperty('height', '72px');
      }
    },
    clonePieChartContent(node, newNode) {
      if (this.isHeaderOrContent(node)) {
        this.removePieChartLayoutHeight(node);
        newNode.width = Boolean(node.clientWidth) && node.clientWidth > newNode.width ? node.clientWidth : newNode.width;
        newNode.height += Boolean(node.clientHeight) ? node.clientHeight : 0;
        newNode.appendChild(node.cloneNode(true));
        this.restorePieChartLayotHeight(node);
      }
    },
    copyHeaderAndPieContent(node, newNode) {
      node.childNodes.forEach(childNode => {
        if (Boolean(childNode.childElementCount)) {
          this.copyHeaderAndPieContent(childNode, newNode);
        }
        this.clonePieChartContent(childNode, newNode);
      });
      newNode.style.setProperty('width', `${newNode.width}px`, 'important');
      newNode.style.setProperty('height', `${newNode.height}px`, 'important');
    },
    copyHeaderAndContent(node, newNode) {
      node.childNodes.forEach(childNode => {
        if (Boolean(childNode.childElementCount)) {
          this.copyHeaderAndContent(childNode, newNode);
        }
        if (this.isHeaderOrContent(childNode)) {
          newNode.width = Boolean(childNode.clientWidth) && childNode.clientWidth > newNode.width ? childNode.clientWidth : newNode.width;
          newNode.height += Boolean(childNode.clientHeight) ? childNode.clientHeight : 0;
          newNode.appendChild(childNode.cloneNode(true));
        }
      });
      newNode.style.setProperty('width', `${newNode.width}px`, 'important');
      newNode.style.setProperty('height', `${newNode.height}px`, 'important');
    },
    copyTextContent(node, newNode) {
      node.childNodes.forEach(childNode => {
        if (Boolean(childNode.childElementCount)) {
          this.copyTextContent(childNode, newNode);
        }
        if (childNode.classList?.contains('annotation-text-card__editor')) {
          newNode.width = (childNode.clientWidth + 10);
          const childNodes = Array.from(childNode.children[0].childNodes);
          newNode.height = childNodes.reduce(
            (totalHeight, childNode) => {
              return totalHeight + childNode.clientHeight;
            },
            30
          );
          newNode.appendChild(childNode.cloneNode(true));
        }
      });
      newNode.style.setProperty('width', `${newNode.width}px`);
      newNode.style.setProperty('height', `${newNode.height}px`);
    },
    copyContent(component, tempDiv) {
      if (component.card?.component === 'AnnotationTextCard') {
        return this.copyTextContent(component.$el, tempDiv);
      }
      if (PIECHART_EXCEPTIONS.includes(component.card?.component)) {
        return this.copyHeaderAndPieContent(component.$el, tempDiv);
      }
      return this.copyHeaderAndContent(component.$el, tempDiv);
    },
    cloneDOMNode(component) {
      const tempDiv = window.document.createElement('div');
      tempDiv.className = `temp-div-${component._uid}`;
      tempDiv.width = 0;
      tempDiv.height = 0;
      tempDiv.style.setProperty('position', 'fixed');
      tempDiv.style.setProperty('top', '0');
      tempDiv.style.setProperty('left', '0');
      tempDiv.style.setProperty('z-index', '-10');
      tempDiv.style.setProperty('display', 'grid');
      tempDiv.style.setProperty('grid-template-rows', 'auto');
      tempDiv.style.setProperty('align-items', 'center');
      this.copyContent(component, tempDiv);
      return tempDiv;
    },
    async getClonedComponentDataURL(component, exportConfig) {
      const clonedDOMNode = this.cloneDOMNode(component);
      window.document.body.appendChild(clonedDOMNode);
      const imageURL = await domToImage.toPng(window.document.getElementsByClassName(`temp-div-${component._uid}`)[0], exportConfig);
      window.document.body.removeChild(clonedDOMNode);
      return imageURL;
    },
    downloadConvertedZip(zipURL) {
      const anchor = window.document.createElement('a');
      anchor.href = `data:application/zip;base64,${zipURL}`;
      anchor.download = 'dashboard.zip';
      window.document.body.appendChild(anchor);
      anchor.click();
      window.document.body.removeChild(anchor);
      this.loadingExportStatus = false;
      trackEventWithUserProperties('ST Dashboard ComponentsExport Export', { Type: EXTENSION_TYPES.PNG.name });
    },
    downloadConvertedImage(dataURL, type, componentName) {
      const anchor = window.document.createElement('a');
      anchor.className = 'downloadImageLink';
      anchor.href = dataURL;
      anchor.download = `${componentName || this.title}.${type.toLowerCase()}`;
      window.document.body.appendChild(anchor);
      anchor.click();
      window.document.body.removeChild(anchor);
    },
  },
};
